/**
 * 신규 처방 등록 다이얼로그.
 * admin-ruda-web `AddPrescriptionModal` 포팅 (presigned → S3 → upload-complete).
 */
import type { BusinessRelationshipResponse } from '@/entities/business/api/business.api';
import { useBusinessRelationshipList } from '@/entities/business/hooks/useBusinessRelationships';
import {
  useCreatePrescriptionPresignedUrls,
  useRegisterPrescriptionUpload,
} from '@/entities/prescription/hooks/usePrescriptions';
import { userService } from '@/entities/user/api/user.api';
import { DatePicker, MonthPicker } from '@/shared/components/DatePicker';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { Button } from '@/shared/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/shared/components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { formatDateValue, formatRefYmValue } from '@/shared/lib/datePickerUtils';
import { uploadFileToPresignedUrl } from '@/shared/utils/fileUpload';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { subMonths } from 'date-fns';
import { X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  expandPrescriptionUploadFiles,
  isPdfUploadFile,
} from './convertPdfToPngPages';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

type UserOption = { email: string; userId: number };

const USER_SEARCH_DEBOUNCE_MS = 300;

/**
 * 거래처 API는 의약품 단위라 같은 병의원이 여러 행으로 온다.
 * SelectItem key/value는 clinicId라, 중복을 제거하지 않으면 React key 경고와
 * Radix Select 컬렉션 깨짐(다이얼로그 안 Date/MonthPicker 클릭 불가)이 난다.
 */
export const uniqueClinicOptions = (
  items: Pick<BusinessRelationshipResponse, 'clinicId' | 'clinicName'>[],
): { clinicId: number; clinicName: string }[] => {
  const seen = new Set<number>();
  const options: { clinicId: number; clinicName: string }[] = [];
  for (const item of items) {
    if (item.clinicId == null || seen.has(item.clinicId)) continue;
    seen.add(item.clinicId);
    options.push({
      clinicId: item.clinicId,
      clinicName: item.clinicName,
    });
  }
  return options;
};

/**
 * 이미 고른 파일명과 같으면 건너뛰고, 새 파일만 목록 뒤에 붙인다.
 */
export const mergePrescriptionUploadFiles = (
  current: File[],
  incoming: File[],
): { files: File[]; added: File[]; skipped: File[] } => {
  const seen = new Set(current.map((file) => file.name));
  const added: File[] = [];
  const skipped: File[] = [];

  for (const file of incoming) {
    if (seen.has(file.name)) {
      skipped.push(file);
      continue;
    }
    seen.add(file.name);
    added.push(file);
  }

  return { files: [...current, ...added], added, skipped };
};

/** 처방 등록 모달 기본 등록일 — 오늘 (yyyy-MM-dd) */
export const getDefaultPrescriptionRegistrationDate = (
  now = new Date(),
): string => formatDateValue(now);

/** 처방 등록 모달 기본 처방년월 — 이전달 (yyyyMM) */
export const getDefaultPrescriptionYm = (now = new Date()): string =>
  formatRefYmValue(subMonths(now, 1));

export const AddPrescriptionDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const toast = useToast();
  const createPresigned = useCreatePrescriptionPresignedUrls();
  const registerUpload = useRegisterPrescriptionUpload();

  const [emailInput, setEmailInput] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState<number | null>(null);
  const [clinicId, setClinicId] = useState('');
  const [registrationDate, setRegistrationDate] = useState('');
  const [prescriptionYm, setPrescriptionYm] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isConvertingPdf, setIsConvertingPdf] = useState(false);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [userListOpen, setUserListOpen] = useState(false);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);

  const { data: relationships } = useBusinessRelationshipList({
    search: {
      email: userEmail || undefined,
      transactionStatus: '승인',
    },
    pageable: { page: 0, size: 1000 },
    // 사용자 선택 전에는 전체 승인 거래처를 끌어와 병의원 Select에 중복 clinicId가 쌓인다
    enabled: open && Boolean(userEmail),
  });

  const clinicOptions = useMemo(
    () => uniqueClinicOptions(relationships?.content ?? []),
    [relationships?.content],
  );

  useEffect(() => {
    if (open) {
      setRegistrationDate(getDefaultPrescriptionRegistrationDate());
      setPrescriptionYm(getDefaultPrescriptionYm());
      return;
    }
    setEmailInput('');
    setUserEmail('');
    setUserId(null);
    setClinicId('');
    setRegistrationDate('');
    setPrescriptionYm('');
    setFiles([]);
    setIsConvertingPdf(false);
    setUserOptions([]);
    setUserListOpen(false);
    setIsSearchingUsers(false);
  }, [open]);

  const searchUsers = async (query = emailInput) => {
    const q = query.trim();
    if (!q) return;
    setIsSearchingUsers(true);
    try {
      const result = await userService.getUsers(
        { email: q, isSalesApproved: true },
        { page: 0, size: 100 },
      );
      const options = result.content
        .map((u) => ({
          email: u.email,
          userId: u.userId ?? u.id ?? 0,
        }))
        .filter((u) => u.userId > 0);
      setUserOptions(options);
      setUserListOpen(true);
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setIsSearchingUsers(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const q = emailInput.trim();
    // 이미 고른 이메일이면 재검색하지 않음
    if (q.length < 2 || q === userEmail) return;
    const timer = window.setTimeout(() => {
      void searchUsers(q);
    }, USER_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // searchUsers는 emailInput을 기본값으로 써서 deps에 넣지 않는다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailInput, open, userEmail]);

  const handleUserSelect = (user: UserOption | null) => {
    if (!user) return;
    setEmailInput(user.email);
    setUserEmail(user.email);
    setUserId(user.userId);
    setClinicId('');
    setUserListOpen(false);
  };

  const handleFilesSelected = (selected: FileList | null) => {
    const incoming = Array.from(selected ?? []);
    if (incoming.length === 0) return;

    const result = mergePrescriptionUploadFiles(files, incoming);
    setFiles(result.files);
    if (result.skipped.length > 0) {
      toast.warning(
        `이미 추가된 파일 ${result.skipped.length}개는 제외했습니다.`,
      );
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((current) => current.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!userId || !clinicId || !registrationDate || !prescriptionYm) {
      toast.error('필수 항목을 모두 입력해주세요.');
      return;
    }
    if (files.length === 0) {
      toast.error('처방 이미지를 선택해주세요.');
      return;
    }

    setIsConvertingPdf(true);
    try {
      // 원본 PDF는 올리지 않고, 등록 시점에 페이지별 PNG로 바꿔 업로드한다
      const result = await expandPrescriptionUploadFiles(files);
      for (const item of result.failed) {
        toast.error(`${item.name} 변환 실패: ${item.message}`);
      }
      if (result.failed.length > 0) {
        return;
      }
      if (result.files.length === 0) {
        toast.error('처방 이미지를 선택해주세요.');
        return;
      }
      for (const item of result.converted) {
        toast.success(
          `${item.name}: ${item.pageCount}개의 페이지가 변환되었습니다.`,
        );
      }

      const uploadFiles = result.files;
      const presignedUrlInfoList = await createPresigned.mutateAsync(
        uploadFiles.map((f) => f.name),
      );
      await Promise.all(
        presignedUrlInfoList.map((info, index) =>
          uploadFileToPresignedUrl(uploadFiles[index], info.presignedUrl),
        ),
      );
      await registerUpload.mutateAsync({
        userId,
        clinicId: Number(clinicId),
        registrationDate,
        prescriptionYm: prescriptionYm.replace(/\D/g, '').slice(0, 6),
        images: presignedUrlInfoList,
      });
      toast.success('처방 정보를 등록했습니다.');
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setIsConvertingPdf(false);
    }
  };

  const isPending =
    createPresigned.isPending || registerUpload.isPending || isConvertingPdf;

  return (
    <Dialog
      open={open}
      // 오버레이·ESC는 Content에서 막고, X·취소만 닫기
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className="max-w-2xl"
        onInteractOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>처방 정보 등록</DialogTitle>
          <DialogDescription>
            사용자·병의원을 선택하고 처방 이미지를 업로드합니다. PDF는 등록 시
            페이지별 PNG로 변환되어 업로드됩니다.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="add-rx-email">사용자 이메일</Label>
            <div className="flex gap-2">
              <div className="min-w-0 flex-1">
                <Combobox
                  items={userOptions}
                  itemToStringValue={(user) => user.email}
                  value={
                    userOptions.find((user) => user.email === userEmail) ?? null
                  }
                  onValueChange={handleUserSelect}
                  open={userListOpen}
                  onOpenChange={setUserListOpen}
                >
                  <ComboboxInput
                    id="add-rx-email"
                    placeholder="사용자 이메일 검색"
                    value={emailInput}
                    onValueChange={(next) => {
                      setEmailInput(next);
                      if (next.trim() !== userEmail) {
                        setUserEmail('');
                        setUserId(null);
                        setClinicId('');
                      }
                    }}
                    onSearch={(query) => {
                      void searchUsers(query);
                    }}
                  />
                  <ComboboxContent>
                    <ComboboxEmpty>
                      {isSearchingUsers
                        ? '검색 중...'
                        : '검색된 사용자가 없습니다.'}
                    </ComboboxEmpty>
                    <ComboboxList<UserOption>>
                      {(user) => (
                        <ComboboxItem key={user.email} value={user}>
                          {user.email}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </div>
              <Button
                type="button"
                className="ruda-button shrink-0"
                onClick={() => void searchUsers()}
              >
                검색
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="add-rx-clinic">병의원</Label>
            <Select
              value={clinicId}
              onValueChange={setClinicId}
              disabled={!userEmail || clinicOptions.length === 0}
            >
              <SelectTrigger id="add-rx-clinic" aria-label="병의원">
                <SelectValue placeholder="병의원 선택" />
              </SelectTrigger>
              <SelectContent>
                {clinicOptions.map((c) => (
                  <SelectItem key={c.clinicId} value={String(c.clinicId)}>
                    {c.clinicName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="add-rx-reg-date">등록일</Label>
            <DatePicker
              id="add-rx-reg-date"
              value={registrationDate}
              onChange={setRegistrationDate}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="add-rx-ym">처방 년월 (yyyyMM)</Label>
            <MonthPicker
              id="add-rx-ym"
              placeholder="년월 선택"
              value={prescriptionYm}
              onChange={setPrescriptionYm}
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="add-rx-files">처방 이미지</Label>
            <FileUploadButton
              inputId="add-rx-files"
              accept="image/*,.pdf"
              multiple
              disabled={isPending}
              onFilesSelected={handleFilesSelected}
            >
              {files.length > 0 ? '파일 추가' : '파일 선택'}
            </FileUploadButton>
            {isConvertingPdf && (
              <p className="text-sm text-muted-foreground">
                PDF를 PNG로 변환하고 있습니다.
              </p>
            )}
            {files.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {files.length}개 파일 선택됨
                </p>
                <ul
                  aria-label="선택한 처방 이미지"
                  className="max-h-40 overflow-y-auto rounded-8 border border-border"
                >
                  {files.map((file, index) => (
                    <li
                      key={`${file.name}-${index}`}
                      className="flex items-center gap-2 border-b border-border px-3 py-1.5 last:border-b-0"
                    >
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                        {file.name}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8 shrink-0"
                        aria-label={`${file.name} 삭제`}
                        disabled={isPending}
                        onClick={() => handleRemoveFile(index)}
                      >
                        <X />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {files.some(isPdfUploadFile) && !isConvertingPdf && (
              <p className="text-sm text-muted-foreground">
                PDF는 등록 시 페이지별 PNG로 변환됩니다.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            취소
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isConvertingPdf ? 'PDF 변환 중...' : '등록'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
